import React, { useCallback, useEffect } from 'react';
import { R } from '../r';
import { usePageFocus } from '@core/components/PageFocusProvider';

export type CallbackFn = () => void;
export function useInterval(callback: CallbackFn, delay: number | null, blurOnFocus = false) {
  const intervalRef = React.useRef<number | null>(null);
  const savedCallback = React.useRef(callback);
  const isFocused = usePageFocus();

  React.useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  React.useEffect(() => {
    const tick = () => savedCallback.current();
    if (R.isNumber(delay)) {
      intervalRef.current = window.setInterval(tick, delay);
      return () => window.clearInterval(intervalRef.current || undefined);
    }
  }, [delay]);

  useEffect(() => {
    if (blurOnFocus && !isFocused && intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    } else if (blurOnFocus && isFocused && !intervalRef.current) {
      const tick = () => savedCallback.current();
      if (R.isNumber(delay)) {
        tick();
        intervalRef.current = window.setInterval(tick, delay);
        return () => window.clearInterval(intervalRef.current || undefined);
      }
    }
  }, [blurOnFocus, delay, isFocused]);

  const reset = useCallback(() => {
    if (intervalRef.current !== null) {
      const tick = () => savedCallback.current();
      if (R.isNumber(delay)) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = window.setInterval(tick, delay);
      }
    }
  }, [delay]);

  const stop = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
    }
  }, []);

  return { intervalRef, reset, stop };
}
